<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 id="title" class="page-title">Prescrições</h1>
        <p class="nd-page-kicker">Receitas, medicamentos e orientações ao paciente.</p>
      </div>
      <v-btn id="btnNew" type="button" color="primary" elevation="0" to="/prescricoes/novo">
        <v-icon left small>mdi-plus</v-icon>
        Incluir
      </v-btn>
    </header>

    <module-stat-grid module="prescriptions" :items="data" :count="count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Prescrições</div>
          <div class="nd-table-toolbar__subtitle">Revise e atualize prescrições emitidas.</div>
        </div>
        <v-form class="nd-table-toolbar__actions" @submit.prevent="load()">
          <v-text-field
            dense
            outlined
            hide-details="auto"
            placeholder="Buscar paciente"
            prepend-inner-icon="mdi-magnify"
            v-model="filter.patientName"
          ></v-text-field>
          <v-btn type="submit" outlined color="primary" elevation="0">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </v-form>
      </div>
            <v-data-table
              :headers="headers"
              :items="data"
              @update:items-per-page="pageSizeChanged"
              @update:page="pageChanged"
              :server-items-length="count"
              :disable-sort="true"
              :loading="loading"
              no-data-text="Nenhuma prescrição encontrada"
              loading-text="Carregando dados..."
              class="app-table hover-table"
              :options.sync="options"
              :footer-props="footerOptions"
            >
              <template v-slot:[`item.patient`]="{ item }">
                <div class="nd-entity-cell">
                  <span class="nd-entity-mark">{{ item.patient && item.patient.name ? item.patient.name.charAt(0) : "P" }}</span>
                  <div>
                    <strong>{{ item.patient ? item.patient.name : '—' }}</strong>
                    <small>ID {{ item.id }}</small>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.items`]="{ item }">
                <span>{{ item.items ? item.items.length : 0 }} item(s)</span>
              </template>

              <template v-slot:[`item.date`]="{ item }">
                <span>{{ formatDate(item.date) }}</span>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="row-actions">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="viewItem(item)">
                        <v-icon size="19">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar prescrição</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="deletePrescription(item)">
                        <v-icon size="19">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Remover prescrição</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
    </v-card>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import axios from "axios";

export default {
  components: { ModuleStatGrid },
  name: "PrescriptionsList",

  data: () => ({
    data: [],
    filter: {
      patientName: "",
    },
    loading: false,
    count: 0,
    headers: [
      { text: "Paciente", value: "patient" },
      { text: "Medicamentos", value: "items" },
      { text: "Data", value: "date" },
      { text: "", value: "actions", sortable: false, align: "end" },
    ],
    options: { page: 1, itemsPerPage: 10 },
    footerOptions: {
      itemsPerPageText: "Itens por página:",
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: "{0}-{1} de {2}",
    },
  }),

  created() {
    this.load();
  },

  computed: {
    medicineCount() {
      return this.data.reduce((total, item) => total + ((item.items && item.items.length) || 0), 0);
    },

    lastRecordDate() {
      const latest = this.data.find((item) => item.date);
      return latest ? this.formatDate(latest.date) : "-";
    },
  },

  methods: {
    pageChanged(page) {
      this.options.page = page;
      this.load();
    },

    pageSizeChanged(size) {
      this.options.itemsPerPage = size;
      this.load();
    },

    async load() {
      this.loading = true;
      try {
        const start = (this.options.page - 1) * (this.options.itemsPerPage || 10);
        const limit = this.options.itemsPerPage || 10;
        const params = { start, limit };
        if (this.filter.patientName) params.patientName = this.filter.patientName;
        const result = await axios.get("/prescriptions", { params });
        this.data = result.data.data;
        this.count = result.data.count;
      } catch (error) {
        this.$dialog.notify.error("Erro ao carregar prescrições.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loading = false;
      }
    },

    viewItem(item) {
      this.$router.push(`/prescricoes/${item.id}`);
    },

    formatDate(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString("pt-BR");
    },

    async deletePrescription(item) {
      const patientName = item.patient ? item.patient.name : `#${item.id}`;
      const proceed = await this.$dialog.confirm({
        text: `Deseja realmente remover a prescrição de "${patientName}"?`,
        title: "Confirmar Exclusão",
        actions: {
          false: "Cancelar",
          true: { text: "Remover", color: "error" },
        },
      });

      if (proceed) {
        this.loading = true;
        try {
          await axios.delete(`/prescriptions/${item.id}`);
          this.$dialog.notify.info("Prescrição removida com sucesso!", { position: "top-right", timeout: 2000 });
          this.load();
        } catch (error) {
          this.$dialog.notify.error("Erro ao remover a prescrição.", { position: "top-right", timeout: 5000 });
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>
